import { useEffect } from 'react'

import _ from 'lodash'
import { useIsClient } from 'usehooks-ts'


import { applyBrandTheme } from '@/shared/lib/color/theme'
import type { DigitalProduct } from '@/types/digitalProduct'

export const useDigitalProductBackground = (
  digitalProduct?: DigitalProduct,
  {
    withBackgroundImage = true,
    withCssVars = true
  }: { withBackgroundImage?: boolean; withCssVars?: boolean } = {}
) => {
  const isClient = useIsClient()

  useEffect(() => {
    if (!isClient) return undefined

    const root = document.documentElement
    const body = document.body

    const apply = () => {
      if (!digitalProduct) return

      if (withBackgroundImage) {
        const windowWidth = Math.max(
          document.documentElement.clientWidth,
          window.innerWidth
        )
        const desktopImage = _.get(
          digitalProduct,
          'background_image_desktop.file.url'
        )
        const mobileImage = _.get(
          digitalProduct,
          'background_image_mobile.file.url',
          desktopImage
        )

        body.style.setProperty('min-height', '100vh')
        body.style.setProperty('min-height', '100dvh')
        body.style.setProperty('background-size', 'cover')
        body.style.setProperty('background-position', 'center')
        body.style.setProperty('background-attachment', 'fixed')
        body.style.setProperty('background-repeat', 'no-repeat')
        body.style.setProperty(
          'background-image',
          `url('${windowWidth < 992 ? mobileImage : desktopImage}')`
        )
      }

      if (withCssVars) {
        /**
         * A cor do evento entra por `--brand` e pelos três tokens medidos —
         * e por mais nada. Os `--bs-primary*` que existiam aqui derivavam por
         * `getShade`, mistura linear em sRGB que desvia o matiz e nunca mede
         * contraste: `--bs-primary-inverse` era `getShade(color, 0.95)`, ou
         * seja, "clareia 95% e torce para ficar legível".
         *
         * Eles saem em vez de conviver. Enquanto os dois caminhos existiam, a
         * marca era roxa e a sombra do botão era da cor do evento — a
         * incoerência que estava em produção.
         */
        applyBrandTheme(root, _.get(digitalProduct, 'color'))

        const logo = document.getElementById('logo') as HTMLImageElement | null
        if (logo && digitalProduct.name && digitalProduct.logo?.file?.url) {
          logo.alt = digitalProduct.name
          logo.title = digitalProduct.name
          logo.src = digitalProduct.logo.file.url
        }
      }
    }

    const debouncedResize = withBackgroundImage ? _.debounce(apply, 120) : null
    if (debouncedResize) window.addEventListener('resize', debouncedResize)
    apply()

    return () => {
      if (debouncedResize) window.removeEventListener('resize', debouncedResize)

      if (withBackgroundImage) {
        body.style.removeProperty('min-height')
        body.style.removeProperty('background-size')
        body.style.removeProperty('background-position')
        body.style.removeProperty('background-attachment')
        body.style.removeProperty('background-repeat')
        body.style.removeProperty('background-image')
      }

      // As vars de marca NÃO são limpas aqui: dentro do app não existe "sair
      // do contexto do evento" — o tenant vem do host, e o host não muda
      // durante a sessão. Devolver a folha ao roxo declarado na desmontagem é
      // a regressão para a marca da plataforma que esta change proíbe.
    }
  }, [digitalProduct, withBackgroundImage, withCssVars])
}
